import React from 'react';
import {
  Card, CardContent, Typography, Box,
  IconButton, alpha, Avatar, Chip
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { motion } from 'framer-motion';

// ─── Dark Cyber Palette (mirrors InternalLayout) ──────────────────────────
const D = {
  bg0:    '#060C18',
  bg1:    '#0B1220',
  bg2:    '#0F172A',
  bg3:    '#141E33',
  bg4:    '#1A2540',

  b300:   '#93C5FD',
  b400:   '#60A5FA',
  b500:   '#3B82F6',
  b600:   '#2563EB',

  c300:   '#67E8F9',
  c400:   '#22D3EE',
  c500:   '#06B6D4',

  t100:   '#F1F5F9',
  t200:   '#CBD5E1',
  t300:   '#94A3B8',
  t400:   '#64748B',

  border1: 'rgba(96,165,250,0.12)',
  border2: 'rgba(96,165,250,0.22)',
  border3: 'rgba(34,211,238,0.25)',
};

// ─── Keyframes injected once ─────────────────────────────────────────────
const HEADER_CSS = `
  @keyframes avatar-pulse {
    0%,100% { box-shadow: 0 0 0 0 rgba(34,211,238,0.35); }
    50%     { box-shadow: 0 0 0 5px rgba(34,211,238,0); }
  }
  @keyframes badge-blink {
    0%,100% { opacity: 1; }
    50%     { opacity: 0.45; }
  }
`;

if (typeof document !== 'undefined' && !document.getElementById('utm-header-css')) {
  const el = document.createElement('style');
  el.id = 'utm-header-css';
  el.innerHTML = HEADER_CSS;
  document.head.appendChild(el);
}

const MotionBox     = motion(Box);
const MotionAvatar  = motion(Avatar);
const MotionIconBtn = motion(IconButton);

const Header = ({
  title,
  subtitle,
  showBackButton  = false,
  onBackClick,
  actionButton,
  icon: HeaderIcon,
  profilePicture  = null,
  isAdmin         = false,
  adminIcon:    AdminIcon,
  verifiedIcon: VerifiedIcon,
  showProfile     = false,
}) => {

  return (
    <MotionBox
      initial={{ opacity: 0, y: -14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, ease: 'easeOut' }}
      sx={{ mb: { xs: 2, sm: 3 } }}
    >
      <Card
        elevation={0}
        sx={{
          borderRadius: '14px',
          background: `linear-gradient(135deg,
            ${alpha(D.bg3, 0.92)} 0%,
            ${alpha(D.bg2, 0.88)} 50%,
            ${alpha(D.bg0, 0.95)} 100%)`,
          backdropFilter: 'blur(24px) saturate(160%)',
          border: `1px solid ${D.border2}`,
          boxShadow: `
            0 8px 32px rgba(0,0,0,0.45),
            0 1px 0 ${alpha(D.c400, 0.08)} inset,
            0 0 0 1px ${alpha(D.b500, 0.04)} inset
          `,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{
          py: { xs: 2, sm: 2.5, md: 3 },
          px: { xs: 1.75, sm: 2.5, md: 3.5 },
          position: 'relative', zIndex: 2,
          '&:last-child': { pb: { xs: 2, sm: 2.5, md: 3 } },
        }}>
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', lg: 'flex-start' },
            gap: { xs: 2, md: 2.5 },
          }}>

            {/* Icon + title + subtitle */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1.25, sm: 2 },
              flex: '1 1 auto',
              minWidth: 0,
              width: { xs: '100%', lg: 'auto' },
              maxWidth: { lg: 'calc(100% - 280px)' },
            }}>

              {/* Back button */}
              {showBackButton && (
                <MotionIconBtn
                  onClick={onBackClick}
                  whileHover={{ scale: 1.07 }}
                  whileTap={{ scale: 0.94 }}
                  sx={{
                    flexShrink: 0,
                    color: D.t200,
                    width: { xs: 36, sm: 40 },
                    height: { xs: 36, sm: 40 },
                    borderRadius: '10px',
                    background: alpha(D.bg4, 0.7),
                    border: `1px solid ${D.border2}`,
                    backdropFilter: 'blur(8px)',
                    transition: 'all 0.22s ease',
                    '&:hover': {
                      color: D.c400,
                      background: alpha(D.c400, 0.1),
                      border: `1px solid ${alpha(D.c400, 0.45)}`,
                      boxShadow: `0 0 12px ${alpha(D.c400, 0.3)}`,
                    },
                  }}
                >
                  <ArrowBackIcon sx={{ fontSize: 19 }} />
                </MotionIconBtn>
              )}

              {/* Profile avatar */}
              {showProfile ? (
                <MotionBox
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.2 }}
                  sx={{ position: 'relative', flexShrink: 0 }}
                >
                  <MotionAvatar
                    src={profilePicture}
                    alt={title}
                    sx={{
                      width: { xs: 52, sm: 62 },
                      height: { xs: 52, sm: 62 },
                      fontFamily: "'Syne', sans-serif",
                      fontWeight: 700,
                      fontSize: { xs: '1.25rem', sm: '1.5rem' },
                      background: `linear-gradient(135deg, ${D.b500}, ${D.c500})`,
                      border: `2px solid ${alpha(D.c400, 0.4)}`,
                      boxShadow: `
                        0 0 0 4px ${alpha(D.c400, 0.07)},
                        0 0 18px ${alpha(D.c400, 0.3)},
                        0 4px 12px rgba(0,0,0,0.4)
                      `,
                      color: '#fff',
                      animation: 'avatar-pulse 3s ease-in-out infinite',
                    }}
                  >
                    {!profilePicture && title?.charAt(0)?.toUpperCase()}
                  </MotionAvatar>

                  {/* Verified badge */}
                  {isAdmin && VerifiedIcon && (
                    <Box sx={{
                      position: 'absolute', bottom: -2, right: -2,
                      width: 22, height: 22, borderRadius: '50%',
                      background: `linear-gradient(135deg, ${D.b500}, ${D.c500})`,
                      border: `2px solid ${D.bg2}`,
                      boxShadow: 'none',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <VerifiedIcon sx={{ fontSize: 12, color: '#fff' }} />
                    </Box>
                  )}
                </MotionBox>

              ) : HeaderIcon && (
                <MotionBox
                  whileHover={{ scale: 1.06 }}
                  transition={{ duration: 0.2 }}
                  sx={{ flexShrink: 0 }}
                >
                  <Avatar sx={{
                    width: { xs: 48, sm: 56 },
                    height: { xs: 48, sm: 56 },
                    background: `linear-gradient(135deg, ${alpha(D.b500, 0.2)}, ${alpha(D.c500, 0.15)})`,
                    border: `1.5px solid ${alpha(D.c400, 0.35)}`,
                    boxShadow: `
                      0 10px 30px rgba(0,0,0,0.35),
                      inset 0 1px 0 ${alpha(D.c300, 0.1)}
                    `,
                    backdropFilter: 'blur(8px)',
                    color: D.c400,
                    transition: 'all 0.22s ease',
                    '&:hover': {
                      boxShadow: `0 18px 50px rgba(0,0,0,0.45)`,
                      border: `1.5px solid ${alpha(D.c400, 0.6)}`,
                    },
                  }}>
                    <HeaderIcon sx={{
                      fontSize: 26,
                      filter: 'none',
                    }} />
                  </Avatar>
                </MotionBox>
              )}

              {/* Title + Subtitle */}
              <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                {/* Title row */}
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  flexWrap: 'wrap',
                  mb: subtitle ? 0.5 : 0,
                }}>
                  <Typography variant="h4" component="h1" sx={{
                    fontFamily: "'Syne', sans-serif",
                    fontWeight: 800,
                    fontSize: { xs: '1.25rem', sm: '1.75rem', md: '2.125rem' },
                    letterSpacing: '-0.02em',
                    lineHeight: 1.2,
                    color: D.t100,
                    textShadow: 'none',
                    textTransform: 'uppercase',
                    wordBreak: 'normal',
                    overflowWrap: 'break-word',
                    whiteSpace: 'normal',
                  }}>
                    {title}
                  </Typography>

                  {/* Admin badge */}
                  {showProfile && isAdmin && AdminIcon && (
                    <Chip
                      icon={<AdminIcon sx={{ fontSize: 12, color: `${D.c400} !important` }} />}
                      label="ADMINISTRATOR"
                      size="small"
                      sx={{
                        height: 22,
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: '0.6rem',
                        fontWeight: 600,
                        letterSpacing: 1.5,
                        background: alpha(D.bg4, 0.9),
                        color: D.c400,
                        border: `1px solid ${alpha(D.c400, 0.4)}`,
                        boxShadow: 'none',
                        backdropFilter: 'blur(8px)',
                        '& .MuiChip-icon': { color: D.c400 },
                        '& .MuiChip-label': { px: 1 },
                      }}
                    />
                  )}

                  {/* Blinking status dot for admin */}
                  {showProfile && isAdmin && (
                    <Box sx={{
                      width: 6, height: 6, borderRadius: '50%',
                      background: D.c400,
                      boxShadow: 'none',
                      animation: 'none',
                      flexShrink: 0,
                    }} />
                  )}
                </Box>

                {/* Subtitle */}
                {subtitle && (
                  <Typography
                    component="p"
                    variant="body2"
                    sx={{
                      fontFamily: "'JetBrains Mono', monospace",
                      color: D.t300,
                      fontSize: { xs: '0.72rem', md: '0.76rem' },
                      fontWeight: 400,
                      letterSpacing: 0.5,
                      lineHeight: 1.5,
                      display: 'block',
                      pl: 1.25,
                      borderLeft: `2px solid ${alpha(D.c400, 0.4)}`,
                      ml: 0.25,
                      mt: 0.25,
                      whiteSpace: 'normal',
                      wordBreak: 'normal',
                      overflowWrap: 'break-word',
                      maxWidth: '100%',
                    }}
                  >
                    {subtitle}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* ── Right: Action buttons ─────────────────────────── */}
            {actionButton && (
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 1,
                flexWrap: 'wrap',
                width: { xs: '100%', md: 'auto' },
                flex: { lg: '0 0 auto' },
                flexShrink: 0,
                alignSelf: { xs: 'stretch', lg: 'center' },
                justifyContent: { lg: 'flex-end' },
                position: 'relative',
                zIndex: 3,
                pt: { xs: 0.5, lg: 0 },
                borderTop: { xs: `1px solid ${alpha(D.border1, 0.8)}`, lg: 'none' },
                '& > *': {
                  width: { xs: '100%', md: 'auto' },
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  flexWrap: 'wrap',
                  gap: 1,
                  justifyContent: { lg: 'flex-end' },
                },
                '& .MuiButton-root': {
                  width: { xs: '100%', sm: 'auto' },
                  flex: { xs: '1 1 auto', sm: '0 0 auto' },
                  minWidth: { xs: 0, sm: 150 },
                  whiteSpace: 'normal',
                  lineHeight: 1.2,
                  minHeight: 44,
                  textAlign: 'center',
                },
                '& .MuiButton-contained': {
                  background: `linear-gradient(135deg, ${D.b500}, ${D.c500})`,
                  color: '#fff',
                  border: 'none',
                  boxShadow: `0 0 14px ${alpha(D.c400, 0.3)}`,
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 600,
                  borderRadius: '9px',
                  textTransform: 'none',
                  transition: 'all 0.22s ease',
                  '&:hover': {
                    background: `linear-gradient(135deg, ${D.b400}, ${D.c400})`,
                    boxShadow: `0 0 22px ${alpha(D.c400, 0.55)}, 0 4px 14px rgba(0,0,0,0.3)`,
                    transform: 'translateY(-1px)',
                  },
                },
                '& .MuiButton-outlined': {
                  color: D.c400,
                  border: `1px solid ${alpha(D.c400, 0.4)}`,
                  background: alpha(D.bg4, 0.5),
                  backdropFilter: 'blur(8px)',
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 500,
                  borderRadius: '9px',
                  textTransform: 'none',
                  transition: 'all 0.22s ease',
                  '&:hover': {
                    border: `1px solid ${alpha(D.c400, 0.75)}`,
                    background: alpha(D.c400, 0.08),
                    boxShadow: `0 0 14px ${alpha(D.c400, 0.25)}`,
                    transform: 'translateY(-1px)',
                  },
                },
              }}>
                {actionButton}
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    </MotionBox>
  );
};

export default Header;
